import type {
  ConversationRouteDecision,
  ConversationRunState,
  ConversationRuntimeEvent,
  RuntimeArtifact,
  RuntimeComputerFrame,
  RuntimeStep,
  RuntimeToolEvent,
} from '@/types/conversationRuntime'

export const initialConversationRunState: ConversationRunState = {
  runId: null,
  conversationId: null,
  status: 'pending',
  mode: null,
  decision: null,
  lastSeqByRun: {},
  streamingText: '',
  persistentText: '',
  steps: [],
  tools: [],
  artifacts: [],
  computerFrames: [],
  memoryUsed: [],
  memorySaved: [],
  approvals: [],
  errors: [],
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined

const upsertById = <T extends { id: string }>(items: T[], next: T): T[] => {
  const index = items.findIndex((item) => item.id === next.id)
  if (index === -1) return [...items, next]
  const copy = items.slice()
  copy[index] = { ...copy[index], ...next }
  return copy
}

const normalizeStep = (data: Record<string, unknown>, fallbackId: string): RuntimeStep => ({
  id: asString(data.id) ?? asString(data.step_id) ?? fallbackId,
  title: asString(data.title) ?? asString(data.label) ?? 'Arbeider med steget',
  status: asString(data.status) as RuntimeStep['status'] ?? 'running',
  summary: asString(data.summary),
  lane: asString(data.lane),
  tools: Array.isArray(data.tools) ? data.tools.filter((tool): tool is string => typeof tool === 'string') : [],
  updates: Array.isArray(data.updates) ? data.updates.filter((update): update is string => typeof update === 'string') : [],
})

const normalizeTool = (data: Record<string, unknown>, fallbackId: string): RuntimeToolEvent => ({
  id: asString(data.id) ?? asString(data.tool_id) ?? fallbackId,
  name: asString(data.name) ?? asString(data.tool) ?? 'tool',
  label: asString(data.label) ?? asString(data.summary) ?? 'Sine jobber',
  status: asString(data.status) as RuntimeToolEvent['status'] ?? 'running',
  lane: asString(data.lane),
  summary: asString(data.summary),
  stepId: asString(data.step_id),
})

const normalizeArtifact = (event: ConversationRuntimeEvent): RuntimeArtifact => {
  const data = asRecord(event.data)
  return {
    id: asString(data.id) ?? `${event.run_id}:${event.seq}`,
    runId: event.run_id,
    name: asString(data.name) ?? asString(data.path) ?? 'artifact',
    type: asString(data.type) ?? 'file',
    path: asString(data.path),
    mimeType: asString(data.mime_type) ?? asString(data.mimeType),
    preview: asString(data.preview),
    downloadUrl: asString(data.download_url) ?? asString(data.downloadUrl),
  }
}

const normalizeComputerFrame = (event: ConversationRuntimeEvent): RuntimeComputerFrame => {
  const data = asRecord(event.data)
  const rawKind = asString(data.kind) ?? asString(data.panel) ?? 'unknown'
  const kind = rawKind === 'browser' || rawKind === 'terminal' || rawKind === 'files' || rawKind === 'approval'
    ? rawKind
    : 'unknown'
  return {
    id: asString(data.id) ?? `${event.run_id}:${event.seq}`,
    kind,
    title: asString(data.title),
    summary: asString(data.summary),
    payload: data,
    timestamp: event.timestamp,
  }
}

export function conversationRuntimeReducer(
  state: ConversationRunState,
  event: ConversationRuntimeEvent,
): ConversationRunState {
  const lastSeq = state.lastSeqByRun[event.run_id] ?? 0
  if (event.seq <= lastSeq) return state

  const nextSeq = {
    ...state.lastSeqByRun,
    [event.run_id]: event.seq,
  }
  const data = asRecord(event.data)
  const base: ConversationRunState = {
    ...state,
    runId: event.run_id,
    conversationId: event.conversation_id ?? state.conversationId,
    status: event.status,
    lastSeqByRun: nextSeq,
  }

  switch (event.type) {
    case 'run.started':
      return {
        ...base,
        mode: asString(data.mode) as ConversationRunState['mode'] ?? base.mode,
        streamingText: '',
        persistentText: '',
        errors: [],
      }

    case 'route.decided':
      return {
        ...base,
        decision: data as unknown as ConversationRouteDecision,
        mode: asString(data.mode) as ConversationRunState['mode'] ?? base.mode,
      }

    case 'message.delta':
      return {
        ...base,
        streamingText: `${state.streamingText}${asString(data.text) ?? asString(data.content) ?? ''}`,
      }

    case 'message.completed': {
      const completedText = asString(data.text) ?? asString(data.content) ?? state.streamingText
      return {
        ...base,
        streamingText: '',
        persistentText: completedText,
        status: 'completed',
      }
    }

    case 'plan.created':
      return {
        ...base,
        steps: Array.isArray(data.steps)
          ? data.steps.map((step, index) => normalizeStep(asRecord(step), `step-${index + 1}`))
          : state.steps,
      }

    case 'step.started':
    case 'step.updated':
    case 'step.completed': {
      const step = normalizeStep(data, `${event.run_id}:step:${event.seq}`)
      const status = event.type === 'step.completed' ? 'completed' : step.status
      return {
        ...base,
        steps: upsertById(state.steps, { ...step, status }),
      }
    }

    case 'tool.started':
    case 'tool.updated':
    case 'tool.completed': {
      const tool = normalizeTool(data, `${event.run_id}:tool:${event.seq}`)
      const status = event.type === 'tool.completed' ? 'completed' : tool.status
      return {
        ...base,
        tools: upsertById(state.tools, { ...tool, status }),
      }
    }

    case 'artifact.created':
      return {
        ...base,
        artifacts: upsertById(state.artifacts, normalizeArtifact(event)),
      }

    case 'computer.frame':
      return {
        ...base,
        computerFrames: upsertById(state.computerFrames, normalizeComputerFrame(event)),
      }

    case 'memory.used':
      return {
        ...base,
        memoryUsed: [...state.memoryUsed, data],
      }

    case 'memory.saved':
    case 'memory.suggested':
      return {
        ...base,
        memorySaved: [...state.memorySaved, data],
      }

    case 'approval.requested':
      return {
        ...base,
        status: 'waiting_approval',
        approvals: [...state.approvals, data],
      }

    case 'run.completed':
      return {
        ...base,
        status: 'completed',
        streamingText: '',
      }

    case 'run.failed':
      return {
        ...base,
        status: 'failed',
        errors: [...state.errors, asString(data.message) ?? 'Runtime feilet'],
      }

    default:
      return base
  }
}
